import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideMarketSize } from './components/SlideMarketSize';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideAIEconomics } from './components/SlideAIEconomics';
import { SlideDifferentiation } from './components/SlideDifferentiation';
import { SlideNewCategory } from './components/SlideNewCategory';
import { SlideStrategy } from './components/SlideStrategy';
import { SlideWhatSnakkerDoes } from './components/SlideWhatSnakkerDoes';
import { SlideSnakkerPlatform } from './components/SlideSnakkerPlatform';
import { SlideFlywheel } from './components/SlideFlywheel';
import { SlidePlatformArchitecture } from './components/SlidePlatformArchitecture';
import { SlideTraction } from './components/SlideTraction';
import { SlideMoat } from './components/SlideMoat';
import { SlideFastCalculator } from './components/SlideFastCalculator';
import { SlideCTA } from './components/SlideCTA';
import { ExecutiveSummary } from './components/ExecutiveSummary';
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const totalSlides = 17;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" />;
      case 2:
        return <SlideMarketSize onContinue={nextSlide} data-id="element-2" />;
      case 3:
        return <SlideProblem onContinue={nextSlide} data-id="element-3" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" />;
      case 5:
        return <SlideAIEconomics onContinue={nextSlide} data-id="element-5" />;
      case 6:
        return <SlideDifferentiation onContinue={nextSlide} data-id="element-6" />;
      case 7:
        return <SlideNewCategory onContinue={nextSlide} data-id="element-7" />;
      case 8:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-8" />;
      case 9:
        return <SlideStrategy onContinue={nextSlide} data-id="element-9" />;
      case 10:
        return <SlideWhatSnakkerDoes onContinue={nextSlide} data-id="element-10" />;
      case 11:
        return <SlideSnakkerPlatform onContinue={nextSlide} data-id="element-11" />;
      case 12:
        return <SlideFlywheel onContinue={nextSlide} data-id="element-12" />;
      case 13:
        return <SlidePlatformArchitecture onContinue={nextSlide} data-id="element-13" />;
      case 14:
        return <SlideMoat onContinue={nextSlide} data-id="element-14" />;
      case 15:
        return <SlideFastCalculator onContinue={nextSlide} data-id="element-15" />;
      case 16:
        return <SlideCTA onContinue={nextSlide} data-id="element-16" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-slate-900" data-id="element-17">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-18" />
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-19">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-20">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>

      {/* Floating Executive Summary button */}
      <button onClick={() => setSummaryOpen(true)} className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#6C2BD9] text-white text-sm font-semibold shadow-lg shadow-[#6C2BD9]/30 hover:bg-[#5A23B8] hover:shadow-xl hover:shadow-[#6C2BD9]/40 hover:-translate-y-0.5 transition-all" aria-label="Open executive summary" data-id="element-21">
        <Sparkles className="w-4 h-4" data-id="element-22" />
        Executive Summary
      </button>

      <ExecutiveSummary open={summaryOpen} onClose={() => setSummaryOpen(false)} data-id="element-23" />
    </div>;
}